import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  ArrowUpRight,
  Check,
  Copy,
  Gift,
  RefreshCw,
  Share2,
  Sparkles,
  Trophy,
  Users,
} from "lucide-react";
import { toast } from "sonner";

import { PageContainer } from "@/components/layout/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useLocalization } from "@/lib/localization";
import { referralCopy } from "@/lib/referralLocalization";
import { useAuth } from "@/lib/store";
import { fetchMyReferrals } from "@/lib/shopApi";

function money(amount = 0, locale = "en-NG", currency = "NGN") {
	return new Intl.NumberFormat(locale, {
		style: "currency", currency, maximumFractionDigits: 0,
	}).format(amount / 100);
}

export default function Referrals() {
  const { session } = useAuth();
  const { language, locale, messages } = useLocalization();
  const copy = referralCopy[language] || referralCopy.en;
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!session?.sessionId || session?.role !== "Owner") return;
    setError("");
    try {
      setData(await fetchMyReferrals(session.sessionId));
    } catch (loadError) {
      setError(loadError?.message || copy.couldNotLoad);
    } finally {
      setLoading(false);
    }
  }, [copy.couldNotLoad, session?.role, session?.sessionId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (session?.role !== "Owner") {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <PageContainer className="mx-auto max-w-[1480px]">
        <div className="space-y-5">
          <Skeleton className="h-16 w-full max-w-md rounded-xl" />
          <div className="grid gap-5 xl:grid-cols-[minmax(0,1.45fr)_380px]">
            <div className="space-y-5">
              <Skeleton className="h-64 rounded-2xl" />
              <Skeleton className="h-80 rounded-2xl" />
            </div>
            <div className="space-y-5">
              <Skeleton className="h-56 rounded-2xl" />
              <Skeleton className="h-64 rounded-2xl" />
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  const code = data?.code?.code || "";
  const link = `${window.location.origin}/?mode=register&ref=${encodeURIComponent(code)}`;
  const share = async () => {
    const payload = {
      title: copy.shareTitle,
      text: copy.shareText(code),
      url: link,
    };
    try {
      if (navigator.share) {
        await navigator.share(payload);
      } else {
        await navigator.clipboard?.writeText(link);
        toast.success(copy.inviteCopied);
      }
    } catch (shareError) {
      if (shareError?.name !== "AbortError") {
        toast.error(copy.couldNotShare);
      }
    }
  };

  const copyCode = async () => {
    await navigator.clipboard?.writeText(code);
    toast.success(copy.codeCopied);
  };

  return (
    <PageContainer className="mx-auto max-w-[1480px]">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary/70">
            {copy.partnerGrowth}
          </div>
          <h1 className="mt-1.5 text-3xl font-semibold tracking-[-0.035em]">{copy.heading}</h1>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            {copy.subtitle}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => void load()}
          className="h-9 w-9 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
          aria-label={`${messages.layout.refreshWorkspace}: ${messages.layout.referrals}`}
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {error ? (
        <div className="mb-5 flex items-center justify-between gap-3 rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={() => void load()}>
            {copy.retry}
          </Button>
        </div>
      ) : null}

      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1.45fr)_380px]">
        <div className="space-y-5">
		  <InviteCard code={code} copy={copy} locale={locale} onCopy={copyCode} onShare={share} settings={data?.settings} />
          <ActivityCard copy={copy} items={data?.items || []} locale={locale} />
        </div>

        <aside className="space-y-5">
          <OverviewCard copy={copy} locale={locale} metrics={data?.metrics} settings={data?.settings} />
          <HowItWorksCard copy={copy} locale={locale} settings={data?.settings} />
        </aside>
      </div>
    </PageContainer>
  );
}

function InviteCard({ code, copy, locale, onCopy, onShare, settings }) {
  return (
    <Card className="relative overflow-hidden rounded-2xl border-primary/10 bg-card shadow-[0_24px_70px_-48px_rgba(6,78,59,0.5)]">
      <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-primary/[0.055] blur-2xl" />
      <div className="pointer-events-none absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-primary via-emerald-400 to-amber-300" />

      <CardContent className="relative p-5 sm:p-6">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-md">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-primary/10 bg-primary/[0.07] text-primary">
              <Sparkles className="h-4.5 w-4.5" />
            </div>
            <h2 className="mt-4 text-xl font-semibold tracking-[-0.025em]">
              {copy.growTitle}
            </h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
			  {copy.inviteHint(money(settings?.rewardAmountMinor, locale, settings?.rewardCurrencyCode), settings?.subscriptionsPerReward || 10)}
            </p>
          </div>

          <div className="w-full rounded-xl border border-primary/10 bg-primary/[0.035] p-3.5 sm:max-w-[300px]">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                {copy.inviteCode}
              </span>
              <Badge variant="outline" className="border-primary/15 bg-background/70 text-[9px] text-primary">
                {copy.private}
              </Badge>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <div className="min-w-0 flex-1 truncate font-mono text-xl font-semibold tracking-[0.16em] text-foreground">
                {code}
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => void onCopy()}
                className="h-9 w-9 shrink-0 rounded-lg border-primary/10 bg-background shadow-none"
                aria-label={copy.copyCode}
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
            <Button
              type="button"
              onClick={() => void onShare()}
              className="mt-3 h-9 w-full rounded-lg bg-primary text-[12px] shadow-sm"
            >
              <Share2 className="h-3.5 w-3.5" />
              {copy.share}
              <ArrowUpRight className="ml-auto h-3.5 w-3.5 opacity-70" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function OverviewCard({ copy, locale, metrics = {}, settings = {} }) {
  return (
    <Card className="rounded-2xl border-border/70 shadow-[0_18px_55px_-48px_rgba(15,23,42,0.6)]">
      <CardHeader className="p-5 pb-3">
        <CardTitle className="text-base tracking-[-0.02em]">{copy.overview}</CardTitle>
        <CardDescription className="text-xs">{copy.overviewHint}</CardDescription>
      </CardHeader>
      <CardContent className="px-5 pb-5">
        <MetricRow icon={Users} label={copy.businessesInvited} value={metrics?.total || 0} />
        <Separator className="my-3 bg-border/60" />
		<MetricRow icon={Trophy} label={copy.verifiedSubscriptions} value={metrics?.qualified || 0} />
        <Separator className="my-3 bg-border/60" />
		<MetricRow icon={Gift} label={copy.cashbackEarned} value={money(metrics?.earnedMinor, locale, settings?.rewardCurrencyCode)} />
		<Separator className="my-3 bg-border/60" />
		<div>
		  <div className="flex justify-between text-[11px] text-muted-foreground"><span>{copy.nextReward}</span><span>{metrics?.progressCount || 0} / {metrics?.progressTarget || 10}</span></div>
		  <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.min(100, ((metrics?.progressCount || 0) / (metrics?.progressTarget || 10)) * 100)}%` }} /></div>
		</div>
      </CardContent>
    </Card>
  );
}

function MetricRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/[0.07] text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1 text-xs text-muted-foreground">{label}</div>
      <div className="shrink-0 font-mono text-sm font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  );
}

function HowItWorksCard({ copy, locale, settings = {} }) {
  const steps = [
    {
      title: copy.share,
      description: copy.step1,
    },
    {
      title: copy.verifiedSubscriptions,
      description: copy.step2,
    },
    {
      title: copy.reward,
		  description: copy.step3(money(settings?.rewardAmountMinor, locale, settings?.rewardCurrencyCode), settings?.subscriptionsPerReward || 10),
    },
  ];

  return (
    <Card className="rounded-2xl border-border/70 shadow-[0_18px_55px_-48px_rgba(15,23,42,0.6)]">
      <CardHeader className="p-5 pb-3">
        <CardTitle className="text-base tracking-[-0.02em]">{copy.howItWorks}</CardTitle>
        <CardDescription className="text-xs">{copy.howItWorksHint}</CardDescription>
      </CardHeader>
      <CardContent className="px-5 pb-5">
        <div className="relative space-y-4">
          <div className="absolute bottom-3 left-[13px] top-3 w-px bg-border" />
          {steps.map((step, index) => (
            <div key={step.title} className="relative flex gap-3">
              <div className="z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary/15 bg-background font-mono text-[10px] font-semibold text-primary">
                {index + 1}
              </div>
              <div className="pt-0.5">
                <div className="text-xs font-semibold text-foreground">{step.title}</div>
                <p className="mt-0.5 text-[11px] leading-4 text-muted-foreground">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ActivityCard({ copy, items, locale }) {
  return (
    <Card className="rounded-2xl border-border/70 shadow-[0_18px_55px_-48px_rgba(15,23,42,0.6)]">
      <CardHeader className="p-5 pb-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base tracking-[-0.02em]">{copy.activity}</CardTitle>
            <CardDescription className="mt-1 text-xs">{copy.activityHint}</CardDescription>
          </div>
          <Badge variant="secondary" className="rounded-full px-2.5 font-mono text-[10px]">
            {items.length}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-5">
        {items.length ? (
          <div className="divide-y divide-border/60">
            {items.map((item) => (
              <div key={item.id} className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{item.shopName}</div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    {copy.joined} · {new Date(item.signedUpAt).toLocaleDateString(locale)}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <Badge
                    variant="outline"
                    className={item.convertedAt
                      ? "gap-1 border-emerald-200 bg-emerald-50 text-[10px] text-emerald-800"
                      : "border-border bg-muted/40 text-[10px] text-muted-foreground"}
                  >
                    {item.convertedAt ? <Check className="h-3 w-3" /> : null}
					{item.qualifies ? copy.subscribed : item.convertedAt ? `${copy.reward}…` : copy.joined}
                  </Badge>
				  {item.rewardAmountMinor > 0 ? (
					<div className="mt-1 font-mono text-[11px] font-medium">
					  {money(item.rewardAmountMinor, locale, item.rewardCurrencyCode)} · {item.rewardStatus}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex min-h-44 flex-col items-center justify-center rounded-xl border border-dashed border-primary/15 bg-primary/[0.025] px-6 text-center">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/[0.07] text-primary">
              <Users className="h-4 w-4" />
            </div>
            <div className="mt-3 text-sm font-medium">{copy.noActivity}</div>
            <p className="mt-1 max-w-xs text-xs leading-5 text-muted-foreground">
              {copy.noActivityHint}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
